// assets/AllCourses/Exercises/SentenceJumblesExercise.jsx

import React, { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function SentenceJumblesExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= NORMALIZED QUESTIONS ================= */
  const questions = Array.isArray(exercise.sentenceJumblesQuestions)
    ? exercise.sentenceJumblesQuestions.map((q) => ({
        type: "jumble",
        correctSentence: q.correctSentence?.trim() || "",
        jumbledWords: Array.isArray(q.jumbledWords)
          ? q.jumbledWords.map((w) => String(w).trim())
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= ENGINE ================= */
  const safeExternalAnswers =
    typeof userAnswer?.answer === "object" ? userAnswer.answer : {};

  const { answers, setAnswer } = useExerciseEngine({
    questions,
    showResult,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC WITH PARENT ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= LOCAL STATE ================= */
  const [index, setIndex] = useState(0);
  const [shuffled, setShuffled] = useState([]);

  const current = questions[index];
  const userOrder = answers[index] || [];

  /* ================= SHUFFLE ================= */
  useEffect(() => {
    if (!current) return;

    const shuffledWords = current.jumbledWords
      .map((word, i) => ({ word, id: i }))
      .sort(() => Math.random() - 0.5);

    setShuffled(shuffledWords);
  }, [index, current]);

  /* ================= DRAG ================= */
  const handleDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", String(id));
  };

  const handleDrop = (e) => {
    if (isPreview || showResult) return;

    e.preventDefault();

    const id = Number(e.dataTransfer.getData("text/plain"));

    if (userOrder.includes(id)) return;

    const nextOrder = [...userOrder, id];

    setAnswer(index, nextOrder);
  };

  /* ================= REMOVE WORD ================= */
  const handleRemove = (id) => {
    if (showResult) return;

    const next = userOrder.filter((i) => i !== id);
    setAnswer(index, next);
  };

  /* ================= BUILD SENTENCE ================= */
  const builtSentence = userOrder
    .map((i) => current?.jumbledWords[i])
    .join(" ");

  const normalize = (text = "", cap = false) => {
    let t = String(text).trim();
    if (!cap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  const isComplete =
    userOrder.length === current?.jumbledWords.length;

  const isCorrect =
    isComplete &&
    normalize(builtSentence, current.requireCapitalization) ===
      normalize(current.correctSentence, current.requireCapitalization);

  /* ================= NEXT ================= */
  const goNext = () => {
    if (index < questions.length - 1) {
      setIndex((i) => i + 1);
    }
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <input
              value={q.correctSentence}
              onChange={(e) => {
                const next = [...questions];
                next[qIdx].correctSentence = e.target.value;
                onUpdate("sentenceJumblesQuestions", next);
              }}
              className="border p-2 w-full"
              placeholder="Correct sentence"
            />

            <div className="flex flex-wrap gap-2">
              {q.jumbledWords.map((w, wIdx) => (
                <input
                  key={wIdx}
                  value={w}
                  onChange={(e) => {
                    const next = [...questions];
                    next[qIdx].jumbledWords[wIdx] = e.target.value;
                    onUpdate("sentenceJumblesQuestions", next);
                  }}
                  className="border p-2"
                />
              ))}
            </div>

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) => {
                const next = [...questions];
                next[qIdx].requireCapitalization = c;
                onUpdate("sentenceJumblesQuestions", next);
              }}
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!current) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= UI ================= */
  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Sentence Jumbles</CardTitle>
        <CardDescription>
          Question {index + 1} / {questions.length}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* WORD BANK */}
        <div className="flex flex-wrap gap-3 justify-center">
          {shuffled.map(({ word, id }) => (
            <Button
              key={id}
              draggable={!showResult}
              onDragStart={(e) => handleDragStart(e, id)}
              disabled={userOrder.includes(id) || showResult}
              variant="secondary"
            >
              {word}
            </Button>
          ))}
        </div>

        {/* DROP ZONE */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={cn(
            "min-h-24 border-2 border-dashed rounded-xl p-6 text-center",
            showResult && isCorrect && "bg-green-100 border-green-400",
            showResult && !isCorrect && "bg-red-100 border-red-400"
          )}
        >
          {userOrder.length === 0 && "Drag words here"}

          <div className="flex flex-wrap gap-2 justify-center">
            {userOrder.map((id) => (
              <Button
                key={id}
                size="sm"
                variant="outline"
                onClick={() => handleRemove(id)}
                disabled={showResult}
              >
                {current.jumbledWords[id]}
              </Button>
            ))}
          </div>
        </div>

        {/* RESULT */}
        {showResult && isComplete && (
          <div className="text-center font-semibold">
            {isCorrect ? (
              <p className="text-green-600">Correct</p>
            ) : (
              <p className="text-red-600">Incorrect</p>
            )}
          </div>
        )}

        {/* NEXT */}
        {!showResult &&
          isComplete &&
          index < questions.length - 1 && (
            <div className="text-center">
              <Button onClick={goNext}>Next</Button>
            </div>
          )}
      </CardContent>
    </Card>
  );
}